import { useRef } from 'react';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { Quote } from 'lucide-react';
import { Reveal, useCountUp, fadeUp, stagger } from './helpers';

const StatCounter = ({ value, suffix = '', prefix = '', decimals = 0, inView }: { value: number; suffix?: string; prefix?: string; decimals?: number; inView: boolean }) => {
    const reduce = useReducedMotion();
    const v = useCountUp(value, inView, 1600);
    const display = reduce ? value : v;
    const formatted = display.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
    return (
        <span className="tabular-nums">
            {prefix}
            {formatted}
            {suffix}
        </span>
    );
};

export const CaseStudy = () => {
    const reduce = useReducedMotion();
    const ref = useRef<HTMLDivElement>(null);
    const inView = useInView(ref, { once: true, margin: '-100px' });

    const timeline = [
        {
            day: 'Day 1',
            title: 'First interview',
            desc: 'A consultant runs a 90-minute session with Sarah. AI suggests follow-ups in real time and drafts 11 short notes from the transcript. Sarah reviews and approves each one.',
        },
        {
            day: 'Day 3',
            title: 'Second and third sessions',
            desc: 'Sarah captures another 11 notes across renewals, escalations, and exec relationships. Her personal Self-Insight document and Identity profile are ready.',
        },
        {
            day: 'Day 6',
            title: 'Her personal documents arrive',
            desc: 'Sarah reads her Knowledge Signature and Opportunity Layer before her last day. She uses the Identity profile in her next interview process.',
        },
        {
            day: 'Day 9',
            title: 'Her replacement starts',
            desc: "The new hire joins. They can ask the captured notes any question in plain English — every answer cited, in Sarah's voice, from her own words.",
        },
        {
            day: 'Day 11',
            title: 'First solo renewal — held',
            desc: "The replacement leads the Acme renewal with Sarah's notes at hand. £1.2M book retained. Zero renewals lost. Sarah's Legacy Report is delivered the same week.",
        },
    ];

    return (
        <section id="case-study" ref={ref} className="py-32 scroll-mt-16 border-t border-border/40">
            <div className="max-w-6xl mx-auto px-6">
                <Reveal className="max-w-2xl mb-14">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-primary font-semibold mb-5">Case study</p>
                    <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-[-0.025em] leading-[1.05]">
                        £<StatCounter value={4.2} decimals={1} inView={inView} suffix="M" /> in renewals held.
                        <span className="text-muted-foreground/60"> Eleven days.</span>
                    </h2>
                </Reveal>

                <Reveal>
                    <div className="rounded-2xl overflow-hidden border border-border/60 grid grid-cols-1 lg:grid-cols-2">
                        <div className="p-10 text-white relative overflow-hidden" style={{ background: 'linear-gradient(160deg, hsl(210 71% 14%) 0%, hsl(221 83% 22%) 100%)' }}>
                            <motion.div
                                className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl opacity-30"
                                style={{ background: 'radial-gradient(circle, hsl(221 83% 53%) 0%, transparent 70%)' }}
                                animate={reduce ? undefined : { scale: [1, 1.1, 1] }}
                                transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
                            />
                            <div className="relative">
                                <div className="flex items-center gap-3 mb-8">
                                    <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center font-bold text-base">N</div>
                                    <div>
                                        <div className="font-semibold text-sm">Northwind Logistics</div>
                                        <div className="text-white/60 text-xs">Mid-market · 1,200 employees</div>
                                    </div>
                                </div>

                                <Quote className="h-7 w-7 text-white/25 mb-4" />
                                <blockquote className="text-white/90 text-lg leading-relaxed mb-8 font-light">
                                    "Sarah held £1.2M of renewals in her head. Handoff captured 22 notes — the reasoning, the signals, the options she'd reject. Her replacement didn't need three
                                    months to ramp. They had the playbook on day one. And Sarah left with documents she still uses on her CV."
                                </blockquote>
                                <div>
                                    <div className="font-semibold text-sm">Maya Patel</div>
                                    <div className="text-white/60 text-xs">VP Customer Success, Northwind Logistics</div>
                                </div>

                                <div className="grid grid-cols-3 gap-4 mt-10 pt-8 border-t border-white/10">
                                    <div>
                                        <div className="text-2xl font-bold text-white">
                                            <StatCounter value={11} inView={inView} />
                                        </div>
                                        <div className="text-white/55 text-xs mt-1">days end-to-end</div>
                                    </div>
                                    <div>
                                        <div className="text-2xl font-bold text-white">
                                            £<StatCounter value={4.2} decimals={1} inView={inView} suffix="M" />
                                        </div>
                                        <div className="text-white/55 text-xs mt-1">book retained</div>
                                    </div>
                                    <div>
                                        <div className="text-2xl font-bold text-white">
                                            <StatCounter value={0} inView={inView} />
                                        </div>
                                        <div className="text-white/55 text-xs mt-1">renewals lost</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-10 bg-card">
                            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-6">The eleven days</div>
                            <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-60px' }} className="space-y-6">
                                {timeline.map((item, i) => (
                                    <motion.div key={item.day} variants={fadeUp} className="flex gap-4">
                                        <div className="flex flex-col items-center">
                                            <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                                            {i < timeline.length - 1 && <div className="w-px flex-1 bg-border mt-2" />}
                                        </div>
                                        <div className="pb-2">
                                            <div className="text-xs font-mono text-primary font-semibold mb-1">{item.day}</div>
                                            <div className="text-sm font-semibold mb-1 leading-snug">{item.title}</div>
                                            <p className="text-[13px] text-muted-foreground leading-relaxed">{item.desc}</p>
                                        </div>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
